import React, { useState, useEffect } from "react";
import { apiClient } from "@/services/apiClient";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { logger } from "@/utils/logger";
import { useAppStore } from "@/core/store/useAppStore";
import { UserRole } from '@/services/urlService';

interface VideoFrameExtractorProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  videoItem: MediaItem;
  clientId: string;
  onFrameExtracted: (newMediaItem: MediaItem) => void;
  onError?: (error: string) => void;
}

export const VideoFrameExtractor: React.FC<VideoFrameExtractorProps> = ({
  videoRef,
  videoItem,
  clientId,
  onFrameExtracted,
  onError,
}) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionSuccess, setExtractionSuccess] = useState(false);
  const { user } = useAppStore();

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    video.addEventListener("timeupdate", handleTimeUpdate);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [videoRef]);

  const handleExtractFrame = async () => {
    if (!videoRef.current || isExtracting) return;

    const timestamp = videoRef.current.currentTime;

    try {
      setIsExtracting(true);
      setExtractionSuccess(false);

      logger.info("🎬 Extracting frame at timestamp:", timestamp);

      const response = await apiClient.extractVideoFrame(videoItem.id, timestamp);

      if (response.success && response.mediaItem) {
        logger.info("✅ Frame extracted successfully:", response.mediaItem);

        // Convert backend response to MediaItem
        const newMediaItem = new MediaItem(
          response.mediaItem,
          UserRole.ADMIN,
          clientId,
          user?.token
        );

        setExtractionSuccess(true);
        onFrameExtracted(newMediaItem);

        setTimeout(() => setExtractionSuccess(false), 3000);
      } else {
        throw new Error("Failed to extract frame");
      }
    } catch (error: any) {
      logger.error("❌ Frame extraction failed:", error);
      const errorMessage = error.response?.data?.error || error.message || "Frame extraction failed";
      onError?.(errorMessage);
    } finally {
      setIsExtracting(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 10);
    return `${mins}:${secs.toString().padStart(2, "0")}.${ms}`;
  };

  return (
    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
      <h4 className="text-sm font-medium text-blue-900 mb-3">
        Extract Frame from Video
      </h4>

      <div className="space-y-4">
        {/* Current Time Display */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-blue-700">Current position:</span>
          <span className="font-mono text-blue-900 font-semibold">
            {formatTime(currentTime)}
          </span>
        </div>

        {/* Instructions */}
        <p className="text-sm text-blue-700">
          Scrub the video to find the perfect frame, then click the button below to extract it as a high-resolution thumbnail.
        </p>

        {/* Extract Button */}
        <button
          onClick={handleExtractFrame}
          disabled={isExtracting}
          className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isExtracting ? (
            <>
              <svg
                className="w-5 h-5 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Extracting Frame...
            </>
          ) : extractionSuccess ? (
            <>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              Frame Extracted Successfully!
            </>
          ) : (
            <>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              Extract Frame at {formatTime(currentTime)}
            </>
          )}
        </button>

        {/* Success Message */}
        {extractionSuccess && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800">
              ✅ High-resolution frame extracted and added to your library! Scroll down to select it as the thumbnail.
            </p>
          </div>
        )}

        <div className="text-xs text-blue-600">
          <strong>Tip:</strong> Pause the video at the perfect moment for the best results.
        </div>
      </div>
    </div>
  );
};
